import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
}

export default function SEO({ title, description, keywords, ogImage, canonical }: SEOProps) {
  const { i18n } = useTranslation();
  const siteUrl = import.meta.env.VITE_APP_URL || 'https://suersdey.com';
  const fullTitle = `${title} | Suersdey`;
  const defaultOgImage = `${siteUrl}/og-image.png`;

  // Structured data for Organization (JSON-LD)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Suersdey',
    alternateName: 'Suersdey Cambodia',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Cambodia\'s leading platform for marketplace, businesses, property, education, jobs, and charity. Empowering communities through technology.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@suersdey.com',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'Khmer'],
    },
    sameAs: [
      'https://www.facebook.com/suersdey',
      'https://www.instagram.com/suersdey/',
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'KH',
      addressLocality: 'Phnom Penh',
    },
  };

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords.join(', ')} />}
      
      {/* Theme color for mobile browser chrome */}
      <meta name="theme-color" content="#2563eb" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage || defaultOgImage} />
      <meta property="og:url" content={canonical || siteUrl} />
      <meta property="og:site_name" content="Suersdey" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage || defaultOgImage} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Hreflang tags for multilingual SEO */}
      <link rel="alternate" hrefLang="en" href={`${siteUrl}/en`} />
      <link rel="alternate" hrefLang="km" href={`${siteUrl}/km`} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
}

export { HelmetProvider };
